import React, { type JSX } from 'react';
import { createPortal } from 'react-dom';

import { useEscapeLayer } from '../../hooks/useEscapeLayer';
import OutsideClickHandler from '../OutsideClickHandler/OutsideClickHandler';

import Actions from './Actions';
import Body from './Body';

import styles from './ConfirmationModal.module.css';

/**
 * Portaled to <body>, like Modal and SlideOutPanel.
 *
 * A confirmation is routinely opened from inside one of those — "End
 * subscription" and "Activate" both come off the subscription side panel — and
 * rendered inline it stayed trapped in the page tree: the panel portals to
 * <body>, so it landed later in document order and, at the same z-index,
 * painted over the confirmation. The buttons were visible but unclickable.
 * A z-index alone does not fix that, because an inline overlay cannot escape
 * an ancestor that creates a stacking context; the portal is what lets it.
 *
 * TaskDetailPanel hit this first and worked around it by portaling at the call
 * site. Doing it here means no caller has to know.
 *
 * Escape cancels it, like a click outside, and only it: it is the top layer
 * of the overlay stack while open, so the panel it came from stays open.
 */
function ConfirmationModal({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}): JSX.Element {
  useEscapeLayer(onClose, true);
  return createPortal(
    <div className={styles.Overlay}>
      <OutsideClickHandler onClickOutside={onClose}>
        <div className={styles.Modal}>{children}</div>
      </OutsideClickHandler>
    </div>,
    document.body,
  );
}

ConfirmationModal.Body = Body;
ConfirmationModal.Actions = Actions;
export default ConfirmationModal;
